import styled from 'styled-components';
import { tokens } from '@cellula/react-theme-patient';
import { IllustrativeSelection } from './_illustrative_selection.component';
import { ExtensiveInfo } from './_extensive_info.component';
import { ComprehensiveInfo } from './_comprehensive_info.component';
import { BottomBanner } from './_bottom_banner.component';
import { CounterState } from '@application/counter';
import { IconProps } from '@cellula/react';
import { Participant, CaseType } from '@feature/cases/common';
import { withTranslation } from '@application/i18n/with_translation';
import { useTrackedIssue } from '@feature/issues/use_tracked_issue';
import { DateService, DateDisplayFormats } from '@application/time/date.service';
import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import classNames from 'classnames';

interface Props {
	issuesTrackedBy: string;
	to: string;
	participants: Participant[];
	isSelected?: boolean;
	reservedUntil?: CounterState;
	showRfv?: string;
	showLabel?: string;
	shownLabelIcon?: IconProps['type'];
	showBannerText?: string;
	showRepresentativeNiN?: string;
	showTime: VisibaDate;
	type: CaseType;
	showReceptionName: string;
}

const elements = {
	container: styled(Link)`
		display: flex;
		flex-direction: column;
		gap: ${tokens.spacing.x1_5};
		width: 100%;
		background: #fff;
		border-radius: 12px;
		padding: ${tokens.spacing.x1_5} ${tokens.spacing.x1_5} ${tokens.spacing.x2};
		position: relative;
		transition: box-shadow 250ms;
		box-shadow: ${tokens.elevation.skim};
		overflow: hidden;

		&:hover {
			box-shadow: ${tokens.elevation.lifted};
		}

		&.is-selected {
			box-shadow: none;
		}
	`,
};

export function CaseCard({
	to,
	issuesTrackedBy,
	isSelected,
	participants,
	reservedUntil,
	showRfv,
	showTime,
	showLabel,
	shownLabelIcon,
	showBannerText,
	showReceptionName,
	showRepresentativeNiN,
	type,
	...rest
}: Props): JSX.Element {
	const host = (participants[0] ?? null) as Participant | null;

	const issuePendingPayment = useTrackedIssue(issuesTrackedBy, ['awaitPayment']);
	const issueFormsNotSigned = useTrackedIssue(issuesTrackedBy, ['awaitFormResponses']);
	const issueUnreadMessages = useTrackedIssue(issuesTrackedBy, ['unreadMessages']);
	const showIsNew = useTrackedIssue(issuesTrackedBy, ['isNewCase']);

	const ariaLabel = useMemo(
		() =>
			getAriaLabel(
				type,
				showTime,
				showRfv,
				showReceptionName,
				issuePendingPayment > 1,
				issueFormsNotSigned,
				issueUnreadMessages,
				showBannerText ?? null,
			),
		[type, showTime, showRfv, showReceptionName, issuePendingPayment, issueFormsNotSigned, issueUnreadMessages, showBannerText],
	);

	return (
		<elements.container
			aria-label={ariaLabel}
			aria-current={isSelected ? 'true' : undefined}
			to={to}
			className={classNames({
				'is-selected': isSelected,
			})}
			{...rest}
		>
			<IllustrativeSelection isSelected={!!isSelected} />
			<ExtensiveInfo
				host={host}
				participantAmount={participants.length}
				receptionName={showReceptionName}
				time={showTime}
				representativeNiN={showRepresentativeNiN}
				rfv={showRfv}
			/>
			<ComprehensiveInfo
				isNew={showIsNew >= 1}
				label={showLabel}
				labelIcon={shownLabelIcon}
				reservedUntil={reservedUntil}
				pendingPayment={issuePendingPayment}
				unreadMessages={issueUnreadMessages}
				formsNotSigned={issueFormsNotSigned}
				stateText={host == null ? withTranslation('PW.Component.CaseCard.Waiting') : undefined}
			/>
			<BottomBanner>{showBannerText}</BottomBanner>
		</elements.container>
	);
}

function getAriaLabel(
	type: CaseType,
	timestamp: VisibaDate,
	rfv: string | undefined,
	reception: string,
	issuePendingPayment: boolean,
	issueFormsNotSigned: number,
	issueUnreadMessages: number,
	lobbyOpenBannerText: string | null,
): string | undefined {
	let typeStr: string;
	switch (type) {
		case 'video':
			typeStr = lobbyOpenBannerText != null ? lobbyOpenBannerText : withTranslation('PW.Component.CaseCard.A11y.TypeAppointment');
			break;
		case 'physical':
			typeStr = withTranslation('PW.Component.CaseCard.A11y.TypePhysical');
			break;
		case 'messaging':
			typeStr = withTranslation('PW.Component.CaseCard.A11y.TypeMessaging');
			break;
	}

	const paymentSection = issuePendingPayment ? withTranslation('PW.Component.CaseCard.A11y.Actions.Payment') : '';

	let formSection = '';
	if (issueFormsNotSigned > 1) {
		formSection = withTranslation('PW.Component.CaseCard.A11y.Actions.AnswerForms', { formCount: issueFormsNotSigned.toString() });
	} else if (issueFormsNotSigned === 1) {
		formSection = withTranslation('PW.Component.CaseCard.A11y.Actions.AnswerForm');
	}

	let messageSection = '';
	if (issueUnreadMessages > 1) {
		messageSection = withTranslation('PW.Component.CaseCard.A11y.Actions.NewMessages', {
			messagesCount: issueUnreadMessages.toString(),
		});
	} else if (issueUnreadMessages === 1) {
		messageSection = withTranslation('PW.Component.CaseCard.A11y.Actions.NewMessage');
	}

	return `${typeStr} ${timestamp.format(
		DateService.getLocaleSpecificDateFormatTemplate(
			timestamp.year() !== DateService.newDate().year()
				? DateDisplayFormats.DEFAULT
				: DateDisplayFormats.FULL_DISPLAY_NO_CURRENT_YEAR_AND_WEEKDAY,
		),
	)}. ${
		rfv != null
			? withTranslation('PW.Component.CaseCard.A11y.RFV', {
					rfvList: rfv,
				})
			: reception
	} ${paymentSection} ${formSection} ${messageSection}`;
}
